import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type AppRole = 'admin' | 'client' | 'prestataire';

// Liste des statuts valides pour considérer un rôle comme actif
const VALID_STATUSES = ['actif', 'active', 'draft', 'prospect'];

interface RoleInfo {
  id: string;
  role: AppRole;
  status: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  created_at: string;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const { toast } = useToast();

  const buildRolesFromStored = (stored: any): RoleInfo[] => {
    try {
      const phone = stored?.user?.phone || stored?.profile?.phone || null;
      const firstName = stored?.userData?.firstName || stored?.profile?.first_name || undefined;
      const lastName = stored?.userData?.lastName || stored?.profile?.last_name || undefined;
      
      // Priorité 1: Si stored.roles existe (structure complète depuis Auth.tsx)
      if (Array.isArray(stored?.roles) && stored.roles.length > 0) {
        console.log('[useAuth] buildRolesFromStored: using stored.roles array:', stored.roles);
        return stored.roles.map((r: any) => ({
          id: r.id,
          role: r.role as AppRole,
          status: r.status || 'active',
          first_name: r.first_name || firstName,
          last_name: r.last_name || lastName,
          email: r.email || stored?.userData?.email || undefined,
          phone: r.phone || phone,
          created_at: r.created_at || new Date().toISOString(),
        }));
      }
      
      // Priorité 2: Construire depuis profile.roleIds si disponible
      const roleIds = stored?.profile?.roleIds;
      if (roleIds) {
        const roles: RoleInfo[] = [];
        const mainRole = stored?.profile?.role || stored?.userType;
        
        if (roleIds.prestataire) {
          roles.push({
            id: roleIds.prestataire,
            role: 'prestataire',
            status: mainRole === 'prestataire' ? 'active' : 'draft',
            first_name: firstName,
            last_name: lastName,
            email: stored?.userData?.email || undefined,
            phone: phone || undefined,
            created_at: new Date().toISOString(),
          });
        }
        if (roleIds.client) {
          roles.push({
            id: roleIds.client,
            role: 'client',
            status: mainRole === 'client' ? 'active' : 'draft',
            first_name: firstName,
            last_name: lastName,
            email: stored?.userData?.email || undefined,
            phone: phone || undefined,
            created_at: new Date().toISOString(),
          });
        }
        if (roleIds.admin) {
          roles.push({
            id: roleIds.admin,
            role: 'admin',
            status: mainRole === 'admin' ? 'active' : 'draft',
            first_name: firstName,
            last_name: lastName,
            email: stored?.userData?.email || undefined,
            phone: phone || undefined,
            created_at: new Date().toISOString(),
          });
        }
        
        if (roles.length > 0) {
          console.log('[useAuth] buildRolesFromStored: built roles from roleIds:', roles);
          return roles;
        }
      }
      
      // Fallback: Construire un seul rôle depuis les données disponibles
      const mainRole: AppRole | null = (stored?.profile?.role
        || (Array.isArray(stored?.profile?.roles) && stored.profile.roles[0])
        || stored?.userType
        || null) as AppRole | null;

      if (!mainRole) {
        console.warn('[useAuth] buildRolesFromStored: no main role found in stored session');
        return [];
      }

      const roleId = stored?.profile?.id || stored?.user?.id || stored?.user?.role_id;
      if (!roleId || roleId === 'ephemeral-user') {
        console.warn('[useAuth] buildRolesFromStored: no valid roleId found');
        return [];
      }

      const role: RoleInfo = {
        id: roleId,
        role: mainRole,
        status: 'active',
        first_name: firstName,
        last_name: lastName,
        email: stored?.userData?.email || stored?.user?.email || undefined,
        phone: phone || undefined,
        created_at: new Date().toISOString(),
      };

      console.log('[useAuth] buildRolesFromStored: built single role from ephemeral session:', role);
      return [role];
    } catch (e) {
      console.warn('[useAuth] buildRolesFromStored: error while building roles', e);
      return [];
    }
  };

  const loadUserRoles = async (phone: string) => {
    try {
      console.log('[useAuth] Loading user roles for phone:', phone);
      // includeDetails=true car l'utilisateur est déjà authentifié (post-MFA)
      const { data, error } = await supabase.functions.invoke('lookup-phone-roles', {
        body: { phone, includeDetails: true }
      });

      console.log('[useAuth] Edge function lookup-phone-roles result:', { data, error });

      if (error) {
        console.error('❌ Error loading user roles:', error);
        return;
      }

      if (data?.exists && data.roles) {
        console.log('[useAuth] Setting roles from edge function:', data.roles);
        setRoles(data.roles);
      } else {
        console.warn('[useAuth] No roles found or edge payload missing roles field');
      }
    } catch (error) {
      console.error('❌ Error in loadUserRoles:', error);
    }
  };

  useEffect(() => {
    let isUnmounted = false;

    const hydrateFromEphemeral = () => {
      const raw = sessionStorage.getItem('quiquoiouquand_auth_session');
      if (!raw) {
        console.log('[useAuth] No ephemeral auth session found in sessionStorage');
        return;
      }

      try {
        const stored = JSON.parse(raw);
        console.log('[useAuth] Hydrating from ephemeral session:', stored);

        if (stored?.authenticated && (stored.user?.phone || stored.profile?.phone)) {
          const phone = stored.user?.phone || stored.profile?.phone;

          const pseudoUser: any = {
            id: stored.user?.id || stored.profile?.id || 'ephemeral-user',
            phone,
            email: stored.user?.email || stored.profile?.email || null,
          };

          console.log('[useAuth] Ephemeral pseudo-user constructed:', pseudoUser);

          setUser(pseudoUser as any);
          setSession({} as any); // pseudo session to pass guards

          // Hydrater immédiatement les rôles à partir de la session éphémère
          const derivedRoles = buildRolesFromStored(stored);
          if (derivedRoles.length > 0) {
            setRoles(derivedRoles);
          }

          if (phone) {
            setTimeout(() => {
              if (!isUnmounted) {
                loadUserRoles(phone);
              }
            }, 0);
          }
        } else {
          console.warn('[useAuth] Ephemeral session found but missing phone or authenticated flag');
        }
      } catch (e) {
        console.warn('⚠️ Unable to parse stored MFA session', e);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (isUnmounted) return;

        console.log('🔐 Auth state change:', event, !!session);

        if (session) {
          setSession(session);
          setUser(session.user ?? null);

          const phone = session.user?.phone;
          if (phone) {
            setTimeout(() => {
              if (!isUnmounted) {
                loadUserRoles(phone);
              }
            }, 0);
          }
        } else {
          // No Supabase session — try to hydrate from ephemeral MFA session
          hydrateFromEphemeral();
        }

        setLoading(false);
        setInitialized(true);
      }
    );

    // Check for existing session once
    const initializeAuth = async () => {
      if (isUnmounted) return;

      const { data: { session } } = await supabase.auth.getSession();
      console.log('🔐 Existing session check:', !!session);

      if (session) {
        setSession(session);
        setUser(session.user ?? null);
        const phone = session.user?.phone;
        if (phone) {
          loadUserRoles(phone);
        }
      } else {
        // Fallback: session issue – use MFA-validated ephemeral session
        hydrateFromEphemeral();
      }

      setLoading(false);
      setInitialized(true);
    };

    initializeAuth();

    return () => {
      isUnmounted = true;
      subscription.unsubscribe();
    };
  }, []); // Ne dépend de rien pour éviter les boucles

  // Écoute la session MFA éphémère (créée après vérification du code) - avec debounce
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const applyFromStorage = async () => {
      const raw = sessionStorage.getItem('quiquoiouquand_auth_session');
      if (!raw) {
        console.log('[useAuth] applyFromStorage: no ephemeral session in storage');
        return;
      }
      try {
        const stored = JSON.parse(raw);
        console.log('[useAuth] applyFromStorage: loaded stored session:', stored);
        if (stored?.authenticated) {
          const pseudoUser: any = {
            id: stored.user?.id || stored.profile?.id || 'ephemeral-user',
            phone: stored.user?.phone || stored.profile?.phone,
            email: stored.user?.email || stored.profile?.email || null,
          };
          console.log('[useAuth] applyFromStorage: pseudoUser:', pseudoUser);
          setUser(pseudoUser as any);
          setSession({} as any);

          // Hydrater immédiatement les rôles à partir de la session éphémère
          const derivedRoles = buildRolesFromStored(stored);
          if (derivedRoles.length > 0) {
            setRoles(derivedRoles);
          }

          const phone = pseudoUser.phone;
          if (phone && roles.length === 0) { // Ne charger que si pas déjà chargé
            console.log('[useAuth] applyFromStorage: loading roles for phone:', phone);
            await loadUserRoles(phone);
          }
        }
      } catch (e) {
        console.warn('[useAuth] applyFromStorage: unable to parse session', e);
      }
    };
    
    const debouncedApply = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(applyFromStorage, 100); // Debounce de 100ms
    };
    
    window.addEventListener('qqoq:auth-updated', debouncedApply);
    // Essaye immédiatement au montage si pas encore de rôles
    if (roles.length === 0) {
      applyFromStorage();
    }
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('qqoq:auth-updated', debouncedApply);
    };
  }, []); // Ne dépend que du montage initial
  
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear ephemeral MFA session
      sessionStorage.removeItem('quiquoiouquand_auth_session');

      setUser(null);
      setSession(null);
      setRoles([]);
      
      toast({
        title: "Déconnexion réussie",
        description: "À bientôt !",
      });
    } catch (error: any) {
      toast({
        title: "Erreur de déconnexion",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const isAuthenticated = () => {
    if (user && session) return true;
    const raw = sessionStorage.getItem('quiquoiouquand_auth_session');
    if (raw) {
      try {
        const stored = JSON.parse(raw);
        return !!stored?.authenticated;
      } catch {}
    }
    return false;
  };
  
  const hasRole = (role: AppRole) =>
    roles.some((r) => {
      const normalizedRole = (r.role || '').toLowerCase();
      const normalizedStatus = (r.status || '').toLowerCase();
      return (
        normalizedRole === role &&
        VALID_STATUSES.includes(normalizedStatus)
      );
    });
  const isAdmin = () => hasRole('admin');
  const isClient = () => hasRole('client');
  const isPrestataire = () => hasRole('prestataire');
  const hasMultipleRoles = () =>
    roles.filter((r) => {
      const normalizedStatus = (r.status || '').toLowerCase();
      return VALID_STATUSES.includes(normalizedStatus);
    }).length > 1;
  
  // Centralized logic to determine the ACTIVE role (selected by user)
  const getActiveRole = (): AppRole | null => {
    // Priority 1: Check explicitly selected role in ephemeral session (userType)
    try {
      const raw = sessionStorage.getItem('quiquoiouquand_auth_session');
      if (raw) {
        const stored = JSON.parse(raw);
        const selectedRole = stored?.userType || stored?.profile?.role || stored?.user?.role;
        if (selectedRole && ['admin', 'client', 'prestataire'].includes(selectedRole)) {
          return selectedRole as AppRole;
        }
      }
    } catch (e) {
      console.error('[useAuth] Error reading session for getActiveRole:', e);
    }

    // Priority 2: Fallback to first active role in roles array (includes prospect)
    const activeRole = roles.find((r) => {
      const normalizedStatus = (r.status || '').toLowerCase();
      return VALID_STATUSES.includes(normalizedStatus);
    });
    if (activeRole) return activeRole.role;

    // Priority 3: Fallback to any role available
    if (roles.length > 0) return roles[0].role;

    return null;
  };

  const getPhone = () => user?.phone || roles[0]?.phone || null;
  const getFirstName = () => roles[0]?.first_name || null;
  const getLastName = () => roles[0]?.last_name || null;
  const getRoleId = (role: AppRole) => roles.find(r => r.role === role)?.id || null;

  // Compatibilité: profile dérivé du premier rôle actif, draft ou prospect
  const activeRoleProfile = roles.find(r => VALID_STATUSES.includes((r.status || '').toLowerCase()));
  const profile = activeRoleProfile ? {
    id: activeRoleProfile.id,
    first_name: activeRoleProfile.first_name,
    last_name: activeRoleProfile.last_name,
    phone: activeRoleProfile.phone,
    email: user?.email || null,
    role: activeRoleProfile.role,
    status: activeRoleProfile.status,
    roles: roles.map(r => r.role),
    roleIds: {
      admin: roles.find(r => r.role === 'admin')?.id,
      client: roles.find(r => r.role === 'client')?.id,
      prestataire: roles.find(r => r.role === 'prestataire')?.id,
    }
  } : null;

  // Compatibilité: userData dérivé des roles
  const userData = roles[0] ? {
    first_name: roles[0].first_name,
    last_name: roles[0].last_name,
    phone: roles[0].phone,
  } : null;

  return {
    user,
    session,
    roles,
    profile,
    userData,
    loading,
    initialized,
    signOut,
    isAuthenticated,
    hasRole,
    isAdmin,
    isClient,
    isPrestataire,
    hasMultipleRoles,
    getActiveRole,
    getPhone,
    getFirstName,
    getLastName,
    getRoleId,
    loadUserRoles
  };
};
